<template>
    <LLoading
        :active="loading"
        :is-full-page="false"
        :is-label-exist="false">
        <div @click="unselectLine">
            <v-chart
                ref="chart"
                :key="JSON.stringify(datasets)"
                :autoresize="isAutoresize"
                :option="option"
                class="chart"
                @downplay="debouncePinLineChart"
                @click="pinSelectedLine"
                @mouseover="assignLineSelection"
                @mouseout="assignLineSelection(null)"
                @legendselectchanged="$emit('legendselectchanged', $event)">
            </v-chart>
        </div>
    </LLoading>
</template>

<script>
  import { defaultLineStyle, defaultOptions } from "@/components/Charts/echart.config";
  import { BarChart, LineChart } from "echarts/charts";
  import { MarkPointComponent } from "echarts/components";
  import { use } from "echarts/core";
  import _cloneDeep from "lodash/cloneDeep";

  import _merge from "lodash/merge";

  import { THEME_KEY } from "vue-echarts";
  import LLoading from "@/components/Common/LLoading.vue";
  import _debounce from "lodash/debounce";
  import { EchartMixin } from "@/components/Charts/echart.mixin";

  use([
    BarChart,
    LineChart,
    MarkPointComponent
  ]);

  export default {
    name: "CommonChartTemplate",
    extends: EchartMixin,
    components: { LLoading },

    props: {
      datasets: {
        type: Array,
        default: () => []
      },

      xLabels: {
        type: Array,
        default: () => []
      },

      loading: {
        type: Boolean,
        default: false
      },

      fullScreenMode: {
        type: Boolean,
        default: false
      },

      tooltipFormatter: {
        type: Function,
        default: null
      },

      legendOptions: {
        type: Object,
        default: () => {}
      },

      customConfig: {
        type: Object,
        default: () => ({})
      }
    },

    data () {
      return {
        pinSeriesName: null,
        seriesIndex: null
      };
    },
    provide: {
      [THEME_KEY]: "light"
    },

    computed: {
      withPercent () {
        return !!this.datasets.find(i => i.isPercent);
      },

      option () {
        return _merge(
          _cloneDeep(defaultOptions),
          {
            legend: {
              data: this.datasets.filter(({ name }) => name != null).map(({ name }) => name),
              formatter: (legendName) => this.datasets.find(({ name }) => name === legendName)?.label,
              left: "center",
              top: 15,
              ...this.legendOptions
            },

            xAxis: {
              type: "category",
              boundaryGap: false,
              data: this.xLabels
            },

            yAxis: [
              {
                type: "value"
              },
              {
                type: "value",
                min: 0,
                max: 100,
                axisLabel: {
                  show: this.withPercent,
                  interval: "auto",
                  formatter: "{value} %"
                }
              }
            ],

            toolbox: this.toolbox,

            tooltip: {
              // TODO: заменить на richText когда PR будет принят
              // https://github.com/apache/echarts/pull/14994
              renderMode: "html",
              axisPointer: {
                type: "line",
                lineStyle: {
                  width: 3,
                  type: "dotted",
                  color: "#BFD2FF"
                }
              },
              formatter: this.tooltipFormatter()
            },

            series: this.datasets.map(({ name, data, color, markPoint, isPercent = false }, index, array) => {
              const degrees = 360 / array.length * index;

              return {
                ...defaultLineStyle,
                name,
                data,
                markPoint,
                emphasis: {
                  focus: "series"
                },
                yAxisIndex: Number(isPercent),
                // areaStyle: {
                //   color: backgroundColor
                // },
                color: color ?? `hsla(${ degrees }deg, 70%, 65%, 1)`
              };
            })
          },
          this.customConfig
        );
      },

      isAutoresize () {
        // echarts bug
        return this.datasets.length > 0;
      },

      debounceUpdateOptions () {
        return _debounce((event) => {
          this.$refs.chart.setOption({
            tooltip: {
              formatter: this.tooltipFormatter(event?.seriesName, this.pinSeriesName)
            }
          });
        }, 20);
      },

      debouncePinLineChart () {
        return _debounce((event) => this.pinLineChart(event), 20);
      }
    },

    methods: {
      unselectLine () {
        this.pinSeriesName = null;
        this.seriesIndex = null;
        this.$refs.chart.setOption({
          tooltip: {
            formatter: this.tooltipFormatter(this.pinSeriesName, this.seriesIndex)
          }
        });
      },

      assignLineSelection (event) {
        this.debounceUpdateOptions(event);
      },

      pinSelectedLine ({ seriesName, seriesIndex, event }) {
        this.seriesIndex = seriesIndex;
        if (this.pinSeriesName && this.pinSeriesName === seriesName) {
          this.pinSeriesName = null;
        } else {
          this.pinSeriesName = seriesName;
        }
        event.stop();
      },

      pinLineChart ({ batch }) {
        batch?.forEach(({ seriesIndex }) => {
          if (seriesIndex !== this.seriesIndex) {
            this.$refs.chart.dispatchAction({
              type: "downplay",
              seriesIndex
            });
          }
        });
        if (!this.pinSeriesName) return;
        this.$refs.chart.dispatchAction({
          type: "highlight",
          seriesName: this.pinSeriesName
        });
      }
    }
  };
</script>

<style scoped lang="scss">
    ::v-deep {
        .chart {
            div:last-of-type {
                z-index: 39 !important;
            }
        }
    }
</style>
